import Alpine from 'alpinejs';
import axios from '../../config/axios.js';

const EMPTY_FORM = {
  section: '',
  title: '',
  description: '',
  link_type: 'none',
  link_target: '',
  sort_order: 0,
  is_active: true,
};

export function createKasirAppContent(baseEndpoint = '/api/v1/kasir/engagement/app-content') {
  return {
    tabs: [
      { key: 'onboarding_slide', label: 'Onboarding' },
      { key: 'dashboard_slider', label: 'Slider Dashboard' },
      { key: 'intro_menu', label: 'Intro Menu' },
      { key: 'intro_image', label: 'Gambar Intro' },
      { key: 'dashboard_popup', label: 'Popup Dashboard' },
      { key: 'feature_menu', label: 'Menu Fitur' },
    ],
    activeTab: 'onboarding_slide',
    items: [],
    linkTypes: [],
    featureSlots: [],
    loading: true,
    saving: false,
    featureSavingKey: null,
    modalOpen: false,
    editing: null,
    form: { ...EMPTY_FORM },
    imageFile: null,
    imagePreview: null,
    featureDrafts: {},

    init() {
      this.fetchMeta();
      this.fetchList();
    },

    switchTab(key) {
      this.activeTab = key;
      if (key === 'feature_menu') {
        this.fetchMeta();
      }
    },

    itemsFor(section) {
      return this.items.filter((row) => row.section === section);
    },

    async fetchMeta() {
      try {
        const { data } = await axios.get(`${baseEndpoint}/meta`);
        this.linkTypes = data.data?.link_types || [];
        const slots = data.data?.feature_menu_slots || [];
        // Draft dulu baru slots — hindari Alpine error saat x-model slot baru
        this.bootstrapFeatureDrafts(slots);
        this.featureSlots = slots;
      } catch (e) {
        console.error(e);
      }
    },

    bootstrapFeatureDrafts(slots = null) {
      const list = Array.isArray(slots) ? slots : this.featureSlots;
      const drafts = { ...this.featureDrafts };
      list.forEach((slot) => {
        if (!slot?.key) return;
        const content = slot.content;
        const prev = drafts[slot.key] || {};
        drafts[slot.key] = {
          title: content?.title || slot.label || '',
          description: content?.description || slot.default_description || '',
          is_active: content ? !!content.is_active : true,
          sort_order: content?.sort_order ?? slot.sort_order ?? 0,
          // Banner halaman fitur
          imageFile: prev.imageFile || null,
          imagePreview: prev.imageFile ? prev.imagePreview : (content?.image_url || null),
          removeImage: false,
          // Icon menu Ushop
          iconFile: prev.iconFile || null,
          iconPreview: prev.iconFile ? prev.iconPreview : (content?.icon_url || null),
          removeIcon: false,
        };
      });
      this.featureDrafts = drafts;
    },

    async fetchList() {
      this.loading = true;
      try {
        const { data } = await axios.get(baseEndpoint);
        const payload = data?.data;
        this.items = Array.isArray(payload) ? payload : (payload?.data || []);
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal memuat konten aplikasi.', 'error');
      } finally {
        this.loading = false;
      }
    },

    linkLabel(type) {
      return this.linkTypes.find((row) => row.value === type)?.label || type || '—';
    },

    needsTitle() {
      return ['onboarding_slide', 'dashboard_slider', 'intro_menu', 'dashboard_popup'].includes(this.form.section);
    },

    needsDescription() {
      return ['onboarding_slide', 'intro_menu', 'dashboard_popup'].includes(this.form.section);
    },

    needsImage() {
      return ['onboarding_slide', 'dashboard_slider', 'intro_menu', 'intro_image', 'dashboard_popup'].includes(this.form.section);
    },

    /** Gambar Intro: hanya upload file — tanpa judul/deskripsi/urutan. */
    needsSortOrder() {
      return this.form.section !== 'intro_image';
    },

    needsActiveToggle() {
      return true;
    },

    imageFieldLabel() {
      if (this.form.section === 'intro_image') return 'File Gambar Intro';
      if (this.form.section === 'intro_menu') return 'Gambar';
      return 'Gambar';
    },

    modalTitle() {
      if (this.form.section === 'intro_image') {
        return this.editing ? 'Ganti Gambar Intro' : 'Upload Gambar Intro';
      }
      return this.editing ? 'Edit Konten' : 'Tambah Konten';
    },

    needsLink() {
      return ['dashboard_slider', 'dashboard_popup'].includes(this.form.section);
    },

    needsLinkTarget() {
      return ['product_detail', 'external'].includes(this.form.link_type);
    },

    openCreate(section) {
      this.editing = null;
      this.form = {
        ...EMPTY_FORM,
        section,
        title: section === 'intro_image' ? 'Gambar Intro' : '',
        link_type: section === 'dashboard_slider' ? 'products' : 'none',
        sort_order: section === 'intro_image' ? 0 : this.itemsFor(section).length,
        is_active: true,
      };
      this.imageFile = null;
      this.imagePreview = null;
      this.modalOpen = true;
    },

    openEdit(item) {
      this.editing = item;
      this.form = {
        section: item.section,
        title: item.title || '',
        description: item.description || '',
        link_type: item.link_type || 'none',
        link_target: item.link_target || '',
        sort_order: item.sort_order ?? 0,
        is_active: !!item.is_active,
      };
      this.imageFile = null;
      this.imagePreview = item.image_url || null;
      this.modalOpen = true;
    },

    closeModal() {
      this.modalOpen = false;
      this.editing = null;
      this.imageFile = null;
      this.imagePreview = null;
    },

    onImageChange(event) {
      const file = event.target.files?.[0];
      this.imageFile = file || null;
      if (file) {
        this.imagePreview = URL.createObjectURL(file);
      }
    },

    onFeatureImageChange(key, event) {
      const file = event.target.files?.[0];
      if (!this.featureDrafts[key]) return;
      this.featureDrafts[key].imageFile = file || null;
      this.featureDrafts[key].removeImage = false;
      if (file) {
        this.featureDrafts[key].imagePreview = URL.createObjectURL(file);
      }
    },

    onFeatureIconChange(key, event) {
      const file = event.target.files?.[0];
      if (!this.featureDrafts[key]) return;
      this.featureDrafts[key].iconFile = file || null;
      this.featureDrafts[key].removeIcon = false;
      if (file) {
        this.featureDrafts[key].iconPreview = URL.createObjectURL(file);
      }
    },

    clearFeatureImage(key) {
      if (!this.featureDrafts[key]) return;
      this.featureDrafts[key].imageFile = null;
      this.featureDrafts[key].imagePreview = null;
      this.featureDrafts[key].removeImage = true;
    },

    clearFeatureIcon(key) {
      if (!this.featureDrafts[key]) return;
      this.featureDrafts[key].iconFile = null;
      this.featureDrafts[key].iconPreview = null;
      this.featureDrafts[key].removeIcon = true;
    },

    buildFormData({ forUpdate = false } = {}) {
      const fd = new FormData();
      Object.entries(this.form).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
        // Update: section prohibited di FormRequest — jangan kirim agar tidak gagal validasi / buat row baru
        if (forUpdate && key === 'section') return;
        if (key === 'is_active') {
          fd.append(key, value ? '1' : '0');
          return;
        }
        fd.append(key, String(value));
      });
      if (this.imageFile) {
        fd.append('image', this.imageFile);
      }
      if (forUpdate) {
        fd.append('_method', 'PUT');
      }
      return fd;
    },

    async save() {
      this.saving = true;
      try {
        if (this.editing?.id) {
          const fd = this.buildFormData({ forUpdate: true });
          const { data } = await axios.post(
            `${baseEndpoint}/${this.editing.id}`,
            fd,
          );
          const updated = data.data?.data || data.data;
          const idx = this.items.findIndex((row) => row.id === this.editing.id);
          if (idx >= 0 && updated) this.items[idx] = updated;
          window.toast?.(data.message || 'Konten diperbarui.', 'success');
        } else {
          const fd = this.buildFormData({ forUpdate: false });
          const { data } = await axios.post(baseEndpoint, fd);
          const created = data.data?.data || data.data;
          if (created) this.items.push(created);
          window.toast?.(data.message || 'Konten ditambahkan.', 'success');
        }
        this.closeModal();
        await this.fetchList();
      } catch (e) {
        const errors = e.response?.data?.errors;
        const firstError = errors
          ? Object.values(errors).flat().find(Boolean)
          : null;
        window.toast?.(
          firstError || e.response?.data?.message || 'Gagal menyimpan konten.',
          'error',
        );
      } finally {
        this.saving = false;
      }
    },

    async saveFeature(slot) {
      const draft = this.featureDrafts[slot.key];
      if (!draft) return;

      const hasBanner =
        draft.imageFile || (!draft.removeImage && (draft.imagePreview || slot.content?.image_url));
      const hasIcon =
        draft.iconFile || (!draft.removeIcon && (draft.iconPreview || slot.content?.icon_url));

      if (!hasBanner && !hasIcon) {
        window.toast?.('Upload minimal salah satu: icon menu atau banner halaman.', 'error');
        return;
      }

      this.featureSavingKey = slot.key;
      try {
        const fd = new FormData();
        fd.append('feature_key', slot.key);
        fd.append('title', draft.title || slot.label);
        if (draft.description) fd.append('description', draft.description);
        fd.append('sort_order', String(draft.sort_order ?? slot.sort_order ?? 0));
        fd.append('is_active', draft.is_active ? '1' : '0');
        if (draft.imageFile) fd.append('image', draft.imageFile);
        if (draft.removeImage) fd.append('remove_image', '1');
        if (draft.iconFile) fd.append('icon', draft.iconFile);
        if (draft.removeIcon) fd.append('remove_icon', '1');

        const { data } = await axios.post(`${baseEndpoint}/feature-menu`, fd);

        this.featureSlots = data.data?.feature_menu_slots || this.featureSlots;
        this.featureDrafts[slot.key] = {
          ...draft,
          imageFile: null,
          removeImage: false,
          imagePreview: data.data?.content?.image_url || null,
          iconFile: null,
          removeIcon: false,
          iconPreview: data.data?.content?.icon_url || null,
        };
        window.toast?.(data.message || 'Image menu fitur disimpan.', 'success');
        await this.fetchList();
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal menyimpan image menu fitur.', 'error');
      } finally {
        this.featureSavingKey = null;
      }
    },

    async remove(item) {
      if (!window.confirm(`Hapus "${item.title || item.section}"?`)) return;
      try {
        const { data } = await axios.delete(`${baseEndpoint}/${item.id}`);
        this.items = this.items.filter((row) => row.id !== item.id);
        window.toast?.(data.message || 'Konten dihapus.', 'success');
        if (item.section === 'feature_menu') {
          await this.fetchMeta();
        }
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal menghapus.', 'error');
      }
    },
  };
}

Alpine.data('kasirAppContent', () => createKasirAppContent('/api/v1/kasir/engagement/app-content'));
